import Alert from "@/components/Alert";
import Layout from "@/components/Layout";
import { Spinner } from "@/components/Spinner";
import useLivlyUser from "@/context/UserProvider";
import { useRouteParams } from "@/hooks/useEncodedIds";
import { BaseLivlyApiResponse } from "@/types/Base";
import { BASE_API_URL } from "@/utils/constants";
import toLocalTime from "@/utils/toLocalTime";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";
import { useSearchParams } from "react-router";

type SurveyResponseOption = {
  surveyQuestionOptionId?: number;
  optionText?: string;
  displayOrder?: number;
  optionType?: string;
  optionTypeId?: number;
  optionTextInputValue?: string | null;
};

type SurveyResponseAnswer = {
  surveyQuestionId?: number;
  questionType?: number | string;
  questionText?: string;
  displayOrder?: number;
  textValue?: string | null;
  numericValue?: number | null;
  npsCategoryValue?: number | null;
  boolValue?: boolean | null;
  dateValue?: string | null;
  selectedOptions?: SurveyResponseOption[];
};

type SurveyResponseDetails = {
  surveyId?: number;
  submittedAt?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  answers?: SurveyResponseAnswer[];
};

async function getSurveyResponseDetails(
  leaseId: number,
  surveyId: number,
  surveyResponseId?: number
) {
  const surveyResponseIdParam = surveyResponseId
    ? `&surveyResponseId=${surveyResponseId}`
    : "";
  const result = await axios.get<BaseLivlyApiResponse<SurveyResponseDetails>>(
    `${BASE_API_URL}/resident/surveys/${surveyId}/response?leaseId=${leaseId}${surveyResponseIdParam}`
  );

  return result.data.Data;
}

function renderAnswerValue(answer: SurveyResponseAnswer, timezone?: string): string {
  const selectedOptions = [...(answer.selectedOptions ?? [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  if (selectedOptions.length > 0) {
    return selectedOptions
      .map((option) => {
        if (option.optionTextInputValue) {
          return `${option.optionText ?? "Other"}: ${option.optionTextInputValue}`;
        }

        return option.optionText ?? "";
      })
      .filter(Boolean)
      .join(", ");
  }

  if (typeof answer.textValue === "string" && answer.textValue.trim().length > 0) {
    return answer.textValue;
  }

  if (typeof answer.numericValue === "number") {
    return String(answer.numericValue);
  }

  if (typeof answer.boolValue === "boolean") {
    return answer.boolValue ? "Yes" : "No";
  }

  if (answer.dateValue) {
    return moment(answer.dateValue).format("MMM D, YYYY");
  }

  return "No response";
}

export default function SurveyResponsePage() {
  const params = useRouteParams<{ leaseId: string; surveyId: string }>();
  const leaseId = Number(params.leaseId);
  const surveyId = Number(params.surveyId);
  const [searchParams] = useSearchParams();
  // Disambiguates which historical response to load when a recurring survey has more than one (HENNY-8456)
  const surveyResponseId = Number(searchParams.get("surveyResponseId")) || undefined;
  const { user } = useLivlyUser();
  const timezone =  user?.building?.timezone
  const { data, isLoading, isError } = useQuery({
    queryKey: ["survey", "response", "readonly", leaseId, surveyId, surveyResponseId],
    queryFn: () => getSurveyResponseDetails(leaseId, surveyId, surveyResponseId),
    enabled: Number.isFinite(leaseId) && Number.isFinite(surveyId),
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div className="h-full flex flex-1 justify-center items-center">
        <Spinner color="livly" size="xl" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="p-6">
        <Alert message="Unable to load survey response. Please try again." />
      </div>
    );
  }

  const answers = [...(data.answers ?? [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );

  return (
    <Layout
      title="Survey Response"
      back={{ to: `/lease/${leaseId}/my-feedback/surveys`, label: "My Surveys" }}
    >
      <div className="max-w-3xl pb-10">
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <p className="text-sm text-gray-600">
              {data.submittedAt
                ? `Submitted ${toLocalTime(data.submittedAt).format("MMM D, YYYY h:mm A")}`
                : "Submitted"}
            </p>
          </div>

          <div className="px-5 py-6 space-y-5">
            {answers.length === 0 ? (
              <Alert variant="default" message="No response answers found for this survey." />
            ) : (
              answers.map((answer, index) => (
                <div
                  key={answer.surveyQuestionId ?? index}
                  className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 overflow-hidden"
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                    Question {index + 1}
                  </p>
<p className="mt-1 text-sm font-medium text-gray-900 break-words whitespace-pre-wrap">
                    {answer.questionText ?? "Question"}
                  </p>
                  {/* break-words/whitespace-pre-wrap keeps long, unbroken responses inside the box (HENNY-8509) */}
                  <p className="mt-2 text-sm text-gray-700 break-words whitespace-pre-wrap">
                    {renderAnswerValue(answer, timezone)}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
